import { useState, useEffect, useCallback } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { useHistoryStore } from '../../stores/historyStore';
import { t } from '../../i18n';
import styles from './PropertiesPanel.module.css';

// Import fabric for side effects
import 'fabric';

const FONT_FAMILIES = [
  'Inter',
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Trebuchet MS',
  'Impact',
  'Comic Sans MS',
];

const FONT_SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72, 96];

export const TextPropertiesPanel = () => {
  const { canvas } = useCanvas();
  const { activeObject, laserOnly } = useDesignerStore();
  const { saveSnapshot } = useHistoryStore();

  const [fontFamily, setFontFamily] = useState('Inter');
  const [fontSize, setFontSize] = useState(36);
  const [fontColor, setFontColor] = useState('#000000');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('center');

  // Check if active object is text
  const isTextObject = activeObject && (activeObject.type === 'textbox' || activeObject.type === 'i-text' || activeObject.type === 'text');

  // Update local state when selection changes
  useEffect(() => {
    if (!isTextObject || !activeObject) return;

    const textObj = activeObject as fabric.Textbox;
    setFontFamily(textObj.fontFamily || 'Inter');
    setFontSize(textObj.fontSize || 36);
    setFontColor((textObj.fill as string) || '#000000');
    setIsBold(textObj.fontWeight === 'bold');
    setIsItalic(textObj.fontStyle === 'italic');
    setIsUnderline(textObj.underline || false);
    setTextAlign((textObj.textAlign as 'left' | 'center' | 'right') || 'center');
  }, [activeObject, isTextObject]);

  const saveToHistory = useCallback(() => {
    if (!canvas) return;
    const state = JSON.stringify(canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']));
    saveSnapshot(state);
  }, [canvas, saveSnapshot]);

  const updateTextProperty = useCallback((property: string, value: unknown) => {
    if (!canvas || !activeObject || !isTextObject) return;

    const textObj = activeObject as fabric.Textbox;
    (textObj as unknown as Record<string, unknown>)[property] = value;
    textObj.setCoords();
    canvas.renderAll();
    saveToHistory();
  }, [canvas, activeObject, isTextObject, saveToHistory]);

  const handleFontFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFontFamily(value);
    updateTextProperty('fontFamily', value);
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10);
    setFontSize(value);
    updateTextProperty('fontSize', value);
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFontColor(value);
    updateTextProperty('fill', value);
  };

  const toggleBold = () => {
    const newValue = !isBold;
    setIsBold(newValue);
    updateTextProperty('fontWeight', newValue ? 'bold' : 'normal');
  };

  const toggleItalic = () => {
    const newValue = !isItalic;
    setIsItalic(newValue);
    updateTextProperty('fontStyle', newValue ? 'italic' : 'normal');
  };

  const toggleUnderline = () => {
    const newValue = !isUnderline;
    setIsUnderline(newValue);
    updateTextProperty('underline', newValue);
  };

  const handleTextAlignChange = (align: 'left' | 'center' | 'right') => {
    setTextAlign(align);
    updateTextProperty('textAlign', align);
  };

  if (!isTextObject) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>{t('design.text.title', 'Text Properties')}</div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.text.font', 'Font')}</label>
        <select
          className={styles.select}
          value={fontFamily}
          onChange={handleFontFamilyChange}
        >
          {FONT_FAMILIES.map((font) => (
            <option key={font} value={font} style={{ fontFamily: font }}>
              {font}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.text.size', 'Size')}</label>
        <select
          className={styles.select}
          value={fontSize}
          onChange={handleFontSizeChange}
        >
          {FONT_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}px
            </option>
          ))}
        </select>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.text.color', 'Color')}</label>
        <div className={styles.colorInputWrapper}>
          <input
            type="color"
            className={styles.colorInput}
            value={fontColor}
            onChange={handleColorChange}
            disabled={laserOnly}
          />
          <span className={styles.colorValue}>{fontColor}</span>
        </div>
        {laserOnly && (
          <span className={styles.hint}>{t('design.laser_white_only', 'Laser mode: White only')}</span>
        )}
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.text.style', 'Style')}</label>
        <div className={styles.buttonGroup}>
          <button
            className={`${styles.styleButton} ${isBold ? styles.active : ''}`}
            onClick={toggleBold}
            title={t('design.text.bold', 'Bold')}
          >
            <strong>B</strong>
          </button>
          <button
            className={`${styles.styleButton} ${isItalic ? styles.active : ''}`}
            onClick={toggleItalic}
            title={t('design.text.italic', 'Italic')}
          >
            <em>I</em>
          </button>
          <button
            className={`${styles.styleButton} ${isUnderline ? styles.active : ''}`}
            onClick={toggleUnderline}
            title={t('design.text.underline', 'Underline')}
          >
            <span style={{ textDecoration: 'underline' }}>U</span>
          </button>
        </div>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.text.alignment', 'Alignment')}</label>
        <div className={styles.buttonGroup}>
          <button
            className={`${styles.styleButton} ${textAlign === 'left' ? styles.active : ''}`}
            onClick={() => handleTextAlignChange('left')}
            title={t('design.align.left', 'Align Left')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="15" y2="12" />
              <line x1="3" y1="18" x2="18" y2="18" />
            </svg>
          </button>
          <button
            className={`${styles.styleButton} ${textAlign === 'center' ? styles.active : ''}`}
            onClick={() => handleTextAlignChange('center')}
            title={t('design.align.center', 'Align Center')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="6" y1="12" x2="18" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          </button>
          <button
            className={`${styles.styleButton} ${textAlign === 'right' ? styles.active : ''}`}
            onClick={() => handleTextAlignChange('right')}
            title={t('design.align.right', 'Align Right')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="9" y1="12" x2="21" y2="12" />
              <line x1="6" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TextPropertiesPanel;
